import React, { useState, useEffect, FocusEvent } from 'react'
import styled from 'styled-components'

interface QuantitySwitchProps {
  quantity: number  
  minValue: number
  maxValue?: number
  disabledIncreasing?: boolean
  isTypable?: boolean
  disabled?: boolean
  onFocus?: () => void
  onBlur?: () => void
  onUpdate: (quantity: number) => void
  onClickIncreaseQuantity?: () => void
  onNoticeOverMaxValue?: () => void
  allowZeroValue?: boolean
}

const capNumberMaximumQuantity = 9999

const QuantitySwitch: React.FC<QuantitySwitchProps> = ({
  quantity,
  minValue = 0,
  maxValue = null,
  onNoticeOverMaxValue = () => { },
  onUpdate = (quantity) => { },
  disabledIncreasing = false,
  isTypable = false,
  disabled = false,
  onFocus = () => { },
  onBlur = () => { },
  onClickIncreaseQuantity = () => { },
  allowZeroValue = true
}) => {
  const [number, setNumber] = useState<number>(allowZeroValue? quantity: quantity < minValue? minValue: quantity)

  useEffect(() => {
    setNumber(allowZeroValue? quantity: quantity < minValue? minValue: quantity)
  }, [quantity, allowZeroValue, minValue])

  let _disabledIncreasing = false
  if (disabledIncreasing) {
    _disabledIncreasing = true
  } else if (maxValue) {
    _disabledIncreasing = quantity >= maxValue ? true : false  
  } else {
    _disabledIncreasing = quantity >= capNumberMaximumQuantity ? true : false
  }

  return (
    <QuantityBox>
      <BtnNumber
        className="decrease-quantity"
        onClick={() => {
          if (((number >= minValue && allowZeroValue) || (number !== minValue && !allowZeroValue)) && !disabled) {
            decrease()
          }
        }}
        disabled={(number <= minValue && !allowZeroValue) || (allowZeroValue && number === 0) || disabled}
      >
        <span>-</span>
      </BtnNumber>
      <Input
        type="text"
        disabled={disabled}
        value={number}
        readOnly={!isTypable}
        onChange={handleOnChangeQuantity}
        onFocus={onFocus}
        onBlur={(e: FocusEvent<HTMLInputElement>) => {
          const number = filterValidQuantity(e.target.value)
          onUpdate(number)
          setNumber(number)
          onBlur()
        }}
        inputMode="numeric"
      />
      <BtnNumber
        className="increase-quantity"
        disabled={_disabledIncreasing}
        onClick={increase}
      >
        <span className="plus">+</span>
      </BtnNumber>
    </QuantityBox>
  )

  function filterValidQuantity(quantity: string) {
    const cleanStrNumber = quantity.replace(/^0|^\s|$\s|\D/i, '')
    let number = parseInt(cleanStrNumber)
    number = number < minValue && allowZeroValue? 0: number < minValue ? minValue : number
    number = maxValue && number > maxValue ? maxValue : number
    number = number ? number : minValue
    number = number > capNumberMaximumQuantity ? capNumberMaximumQuantity : number
    return number
  }

  function handleOnChangeQuantity(e: React.FormEvent<HTMLInputElement>) {
    if (e.currentTarget.value !== '') {
      const number = filterValidQuantity(e.currentTarget.value)
      onUpdate(number)
      setNumber(number)
      onBlur()
    } else {
      setNumber(0)
      onFocus()
    }
  }

  function decrease() {
    const _number = number < minValue + 1 && allowZeroValue? 0: number < minValue + 1 ? minValue : number - 1
    onUpdate(_number)
    setNumber(_number)
  }

  function increase() {
    if (maxValue !== null && number + 1 > maxValue) {
      onNoticeOverMaxValue()
    }

    if ((maxValue !== null && number > maxValue - 1) || _disabledIncreasing) {
      return false
    } else {
      const _number = number === 0 && minValue? number + minValue: number + 1
      onUpdate(_number)
      setNumber(_number)
      onClickIncreaseQuantity()
    }
  }
}

export default QuantitySwitch

const QuantityBox = styled.div`
  display: flex;
  align-items: center;
`

const BtnNumber = styled.div<{ disabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border: 1px solid #CCC;
  transition: 0.6s;
  background-color: white;
  cursor: pointer;
  user-select: none;
  font-size: 20px;

  @media (max-width: 1024px) {
    height: 40px;
    min-width: 53px;
  }

  @media (max-width: 767px) {
    height: 32px;
    min-width: 65px;
  }

  &:first-child {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }

  &:last-child {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  i {
    transition: 0.6s;
    color: black;
  }

  .minus {
  }

  .plus {
    position: relative;
    top: 1px;
  }

  &:active {
    transition: 0s;
    background-color: var(--global--color-background-secondary);

    i {
      transition: 0s;
      color: white;
    }
  }

  ${({ disabled }) =>
    disabled
      ? `
    background-color: #EEE !important;
    box-shadow: none !important;
  `
      : ''}
`

const Input = styled.input<{ readOnly: boolean }>`
  display: block !important;
  border: 0 !important;
  outline: none !important;
  height: 50px !important;
  line-height: 50px !important;
  width: 140px !important;
  text-align: center!important;
  font-size: 20px !important;
  color: black !important;
  border: 1px solid #CCC !important;
  margin-left: -1px !important;
  margin-right: -1px !important;

  @media (max-width: 1024px) {
    height: 40px !important;
    line-height: 40px !important;
    width: 100% !important;
  }

  @media (max-width: 767px) {
    height: 32px !important;
    line-height: 32px !important;
  }

  :disabled {
    color: #CCC !important;
  }
`
